import { useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  appendProfileGenTestResults,
  ProfileGenTestCaseViewParsed,
  selectLensEditorFieldAiConfigs,
  selectProfileGenTestCases,
  selectProfileGenTestResults,
  setProfileGenTestResults,
  TestCaseLabels,
} from "redux/slices/lensEditor";
import {
  Button,
  TextField,
  FormControl,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { AppRoutes } from "AppRoutes";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { pluralize } from "helpers/coll";
import { useParamNumbers } from "helpers/hooks/useParamNumbers";
import { selectUsers } from "redux/slices/client";
import { fullName } from "helpers/name-helpers";
import CheckAll from "routes/lenses/lens/files/CheckAll";
import PDatePicker from "components/PDatePicker";
import { startOfDay } from "date-fns";
import { SubscriptionSuccess, isComplete, isGqlError, isSuccess, useGqlSubscription } from "gqlClient";
import { SubAiChatTestProfileGenDocument, SubAiChatTestProfileGenSubscription } from "generated/graphql";
import { FormFieldSelect } from "components/FormFieldSelect";
import TestRunSummary from "./TestRunSummary";
import TestCase from "./TestCase";
import PromptViewer from "./PromptViewer";
import useTestCaseManager from "./useTestCaseManager";
import SelectTestLabels from "./SelectTestLabels";

function compareValueGroupUI(a: ProfileGenTestCaseViewParsed, b: ProfileGenTestCaseViewParsed): boolean {
  return a.id === b.id;
}

type TestCaseState = {
  expanded: boolean;
  loading: boolean;
};

const ProfileGenerationTestManager = () => {
  const { lensId, fieldId } = useParamNumbers(["lensId", "fieldId"]);
  const dispatch = useAppDispatch();
  const fieldAiConfigs = useAppSelector(selectLensEditorFieldAiConfigs);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { handleDelete } = useTestCaseManager();
  const [selectedTestCases, setSelectedTestCases] = useState<ProfileGenTestCaseViewParsed[]>([]);
  const selectedIds = selectedTestCases.map((tc) => tc.id);
  const testCasesByField = useAppSelector(selectProfileGenTestCases);
  const currentResults = useAppSelector(selectProfileGenTestResults);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState<string>();
  const [dateTo, setDateTo] = useState<string>();
  const [rowState, setRowState] = useState<Record<string, TestCaseState>>({});
  const [labelFilterState, setLabelFilterState] = useState<Record<string, boolean | undefined>>({});
  const [promptId, setPromptId] = useState<string>();
  const [showResults, setShowResults] = useState<boolean>(false);
  const [runComplete, setRunComplete] = useState<boolean>(false);
  const [runSize, setRunSize] = useState<number | undefined>();

  if (lensId === undefined || fieldId === undefined) {
    return <p>No lens or field id</p>;
  }

  const testCases = testCasesByField[fieldId] ?? [];
  const filteredTestCases = testCases.filter((testCase) => {
    const lowerSearch = search.toLocaleLowerCase();

    if (
      testCase.descriptionText.toLocaleLowerCase().indexOf(lowerSearch) === -1 &&
      testCase.caseId.toString().indexOf(lowerSearch) === -1 &&
      testCase.sourceAiChatId.toLocaleLowerCase().indexOf(lowerSearch) === -1
    ) {
      return false;
    }

    const isBeforeFromDate = dateFrom && startOfDay(new Date(testCase.createdAt)).toISOString() < dateFrom;
    const isAfterToDate = dateTo && startOfDay(new Date(testCase.createdAt)).toISOString() > dateTo;

    if (isBeforeFromDate || isAfterToDate) {
      return false;
    }

    for (const label of Object.values(TestCaseLabels)) {
      const isFiltered = Object.hasOwn(labelFilterState, label);
      const isNegated = isFiltered && !!labelFilterState[label];
      if (isFiltered && isNegated && testCase.labels.includes(label)) {
        return false;
      } else if (isFiltered && !isNegated && !testCase.labels.includes(label)) {
        return false;
      }
    }
    return true;
  });

  const userNameLookup = useAppSelector(selectUsers).reduce(
    (acc, user) => {
      acc[user.id] = fullName(user);
      return acc;
    },
    {} as { [key: number]: string }
  );

  function updateStateFragment(testCaseIds: number[], updater: (state: TestCaseState) => Partial<TestCaseState>) {
    const nextState = {};

    for (const id of testCaseIds) {
      const currentTestState = rowState[id] || { expanded: false, loading: false };
      nextState[id] = { ...currentTestState, ...updater(currentTestState) };
    }

    setRowState((currentState) => ({ ...currentState, ...nextState }));
  }

  const toggleRowExpanded = (testCaseIds: number[]) =>
    updateStateFragment(testCaseIds, (currentState) => ({
      ...currentState,
      expanded: !currentState.expanded,
    }));

  const setRowLoadingState = (testCaseIds: number[], loading: boolean) =>
    updateStateFragment(testCaseIds, (currentState) => ({ ...currentState, loading }));

  const setRowExpansionState = (testCaseIds: number[], expanded: boolean) =>
    updateStateFragment(testCaseIds, (currentState) => ({ ...currentState, expanded }));

  const expandAll = () =>
    setRowExpansionState(
      filteredTestCases.map((tc) => tc.id),
      true
    );

  const collapseAll = () =>
    setRowExpansionState(
      filteredTestCases.map((tc) => tc.id),
      false
    );

  const subscription = useGqlSubscription(
    SubAiChatTestProfileGenDocument,
    { fieldId, lensId, testCaseIds: selectedIds },
    (messageBuffer) => {
      const successes: SubscriptionSuccess<SubAiChatTestProfileGenSubscription>[] = [];

      for (const msg of messageBuffer) {
        if (isGqlError(msg)) {
          enqueueSnackbar(msg.error.message, { variant: "error" });
        } else if (isSuccess(msg)) {
          successes.push(msg);
        } else if (isComplete(msg)) {
          setRunComplete(true);
        }
      }
      const testCaseIds = successes.map((msg) => msg.data.aiChatTestProfileGen.testCaseId);
      const testCases = successes.map((msg) => msg.data.aiChatTestProfileGen);
      setRowLoadingState(testCaseIds, false);
      setRowExpansionState(testCaseIds, true);
      dispatch(appendProfileGenTestResults({ testResults: testCases }));
    }
  );

  const handleRunTests = () => {
    dispatch(setProfileGenTestResults({ testResults: [] }));
    setRowLoadingState(selectedIds, true);
    subscription.subscribe();
    setShowResults(true);
    setRunComplete(false);
    setRunSize(selectedIds.length);
  };

  const toggleSelectTestCase = (testCase: ProfileGenTestCaseViewParsed) => {
    setSelectedTestCases((prevSelected) =>
      prevSelected.find((ps) => ps.id === testCase.id) !== undefined
        ? prevSelected.filter((selected) => selected.id !== testCase.id)
        : [...prevSelected, testCase]
    );
  };

  return (
    <div className="px-4">
      <div className="flex flex-wrap pt-2 gap-y-2 gap-x-4">
        <p className="font-semibold min-w-32">
          {filteredTestCases.length} Test {pluralize(filteredTestCases.length, "Case")}
        </p>
        <FormFieldSelect
          fieldId={fieldId}
          label="Field"
          changeFieldId={(fieldId) => {
            fieldId && navigate(AppRoutes.aiTestsField(lensId, fieldId));
          }}
          filterOption={(field) => !!fieldAiConfigs.find((c) => c.fieldId === field.id)}
        />
        <FormControl>
          <PDatePicker
            label="From"
            date={dateFrom ? new Date(dateFrom) : undefined}
            setDate={(d) => setDateFrom(d ? d.toISOString() : undefined)}
          />
        </FormControl>
        <FormControl>
          <PDatePicker
            label="To"
            date={dateTo ? new Date(dateTo) : undefined}
            setDate={(d) => setDateTo(d ? d.toISOString() : undefined)}
          />
        </FormControl>
        <SelectTestLabels setLabelFilterState={setLabelFilterState} />
        <FormControl>
          <TextField
            label="Search"
            placeholder="Description or test case id"
            value={search}
            onChange={(evt) => setSearch(evt.target.value)}
          />
        </FormControl>
        <div className="flex items-center">
          <p className="pattern-link" onClick={collapseAll}>
            Collapse All
          </p>
        </div>
        <div className="flex items-center">
          <p className="pattern-link" onClick={expandAll}>
            Expand All
          </p>
        </div>
        <div className="grow" />
        <Button variant="contained" disabled={selectedIds.length === 0} onClick={handleRunTests}>
          Run tests
        </Button>
      </div>
      {showResults && runSize && (
        <TestRunSummary
          results={currentResults}
          count={runSize}
          onDismiss={() => setShowResults(false)}
          running={!runComplete}
        />
      )}
      <TableContainer className="mt-2 bg-white rounded-md shadow">
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell className="w-16">
                <CheckAll<ProfileGenTestCaseViewParsed>
                  selectedItems={selectedTestCases}
                  items={filteredTestCases}
                  setItems={setSelectedTestCases}
                  compareFn={compareValueGroupUI}
                />
              </TableCell>
              <TableCell className="w-96 min-w-96">Description</TableCell>
              <TableCell className="w-24" align="center">
                Status
              </TableCell>
              <TableCell>Links</TableCell>
              <TableCell className="w-48">Created On</TableCell>
              <TableCell className="w-24">Created By</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTestCases.map((testCase) => {
              const createdByUserId = testCase.createdBy.split(":")[2];
              const createdBy = createdByUserId && userNameLookup[createdByUserId];
              return (
                <TestCase
                  key={testCase.id}
                  testCase={testCase}
                  lensId={lensId}
                  handleDelete={() => handleDelete(testCase)}
                  selectedIds={selectedIds}
                  toggleSelectTestCase={() => toggleSelectTestCase(testCase)}
                  createdBy={createdBy}
                  expanded={!!rowState[testCase.id]?.expanded}
                  loading={!!rowState[testCase.id]?.loading}
                  toggleExpanded={() => toggleRowExpanded([testCase.id])}
                  setPromptId={setPromptId}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {promptId && <PromptViewer chatId={promptId} lensId={lensId} setPromptId={setPromptId} />}
    </div>
  );
};

export default ProfileGenerationTestManager;
